// ============================================================
// TMR Portal Admin — authorization tooling reads and writes, over @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import type { Paged } from '@tmr/api-client';
import { authorizationKeys, holdingKeys } from '../queryClient';
import { exportPageReader } from '../../lib/export/exportPageReader';
import type {
  AccessRequestInviteLink,
  AccessRequestListItem,
  AccessRequestStatus,
  ApproveAccessRequest,
  AuditLogEntry,
  AuthorizationRole,
  CatalogFeature,
  CreateRoleResult,
  HoldingOption,
  ReissueInviteResult,
  RoleWriteRequest,
  SyncAccessRequestsResult,
} from './contract';

/** How many rows the audit log and the approval queue show per page. */
export const AUTHORIZATION_PAGE_SIZE = 15;

/**
 * The permission catalogue, grouped by Feature (10 B7a) — what the role editor offers as checkboxes.
 *
 * Permissions are a system catalogue created by migration, never by this screen (06 §5.2), so the
 * catalogue only changes on deploy; it is cached for the session rather than refetched per edit.
 */
export function useFeatureCatalog(): UseQueryResult<CatalogFeature[]> {
  return useQuery({
    queryKey: authorizationKeys.features(),
    queryFn: ({ signal }) => api.get<CatalogFeature[]>('/authorization/features', { signal }),
    staleTime: Infinity,
  });
}

/** Every role, system and custom, with its direct and effective permissions (10 B7a). Not paginated. */
export function useAuthorizationRoles(): UseQueryResult<AuthorizationRole[]> {
  return useQuery({
    queryKey: authorizationKeys.roles(),
    queryFn: ({ signal }) => api.get<AuthorizationRole[]>('/authorization/roles', { signal }),
  });
}

/**
 * One role, read fresh before editing it.
 *
 * The list already carries the same shape, but `editable` is computed against the caller's **fresh**
 * permissions on every request (10 B7a): opening the editor on a list loaded minutes ago could offer to
 * edit a role the operator may no longer write.
 *
 * @param roleId The role to read, or undefined while nothing is selected.
 */
export function useAuthorizationRole(roleId: string | undefined): UseQueryResult<AuthorizationRole> {
  return useQuery({
    queryKey: authorizationKeys.role(roleId ?? ''),
    queryFn: ({ signal }) => api.get<AuthorizationRole>(`/authorization/roles/${roleId}`, { signal }),
    enabled: Boolean(roleId),
  });
}

/**
 * Creates a custom role.
 *
 * Every write here bumps the authorization version stamp on the backend, and shows up in the audit log —
 * so the whole `authorization` key is invalidated, not just the role list.
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RoleWriteRequest) =>
      api.post<CreateRoleResult>('/authorization/roles', request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/**
 * Replaces a custom role's name, parent and permissions.
 *
 * The permission list is wholesale: a key left out is revoked, not kept. The editor therefore submits
 * the full set it is showing, never a delta.
 *
 * @param roleId The role being edited.
 */
export function useUpdateRole(roleId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RoleWriteRequest) =>
      api.put<void>(`/authorization/roles/${roleId}`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/**
 * Deletes a custom role.
 *
 * The server refuses (409) while anyone still holds the role or another role inherits from it — the
 * screen shows that as the corrective action, since the operator has to revoke or re-parent first.
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId: string) => api.delete<void>(`/authorization/roles/${roleId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/** Filters the audit log accepts; part of the cache key, so a change refetches. */
export interface AuditLogFilters {
  /** Entity name, matched exactly as the backend records it (`Role`, `RolePermission`, …). */
  entity?: string;
  /** Restricts the log to one operator's writes. */
  actorUserId?: string;
  page?: number;
}

/**
 * One page of the authorization audit log, most recent first (10 B7c).
 *
 * @param filters Entity, actor and page; every change is a new cache entry.
 */
export function useAuditLog(filters: AuditLogFilters = {}): UseQueryResult<Paged<AuditLogEntry>> {
  const query = {
    entity: filters.entity,
    actorUserId: filters.actorUserId,
    page: filters.page ?? 1,
    pageSize: AUTHORIZATION_PAGE_SIZE,
  };

  return useQuery({
    queryKey: authorizationKeys.auditLog(query),
    queryFn: ({ signal }) => api.get<Paged<AuditLogEntry>>('/authorization/audit-log', { query, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Reads one page of the audit log **export** (20 §3) — the same filters the table is using.
 *
 * @param filters The screen's active filters.
 */
export function auditLogExportPage(filters: AuditLogFilters = {}) {
  return exportPageReader<AuditLogEntry>('/authorization/audit-log/export', {
    entity: filters.entity,
    actorUserId: filters.actorUserId,
  });
}

/** Filters the approval queue accepts. */
export interface AccessRequestFilters {
  status?: AccessRequestStatus;
  page?: number;
}

/**
 * One page of the access approval queue (10 B7d).
 *
 * `enabled` is how the screen says the surface exists at all: the three `access-requests` routes are only
 * mapped when the backend has a Clerk secret key, and `capabilities.accessRequests` reports that from the
 * same configuration. Asking anyway would spend a request to get a 404 that means several other things
 * too — which is exactly the inference this replaced.
 *
 * @param filters Status and page.
 * @param enabled False when the environment has no access-approval surface; the query does not run.
 */
export function useAccessRequests(
  filters: AccessRequestFilters = {},
  enabled = true,
): UseQueryResult<Paged<AccessRequestListItem>> {
  const query = {
    status: filters.status,
    page: filters.page ?? 1,
    pageSize: AUTHORIZATION_PAGE_SIZE,
  };

  return useQuery({
    queryKey: authorizationKeys.accessRequests(query),
    queryFn: ({ signal }) =>
      api.get<Paged<AccessRequestListItem>>('/authorization/access-requests', { query, signal }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** Pulls the Clerk Waitlist into the queue. Idempotent — an entry already materialized is not created twice. */
export function useSyncAccessRequests() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<SyncAccessRequestsResult>('/authorization/access-requests/sync'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/**
 * Approves a request: invites the person in Clerk, then creates the account and grants the roles.
 *
 * The Clerk call happens first on the backend, so a 502 means nothing was written and the request is
 * still pending — safe to retry once the upstream recovers (06 §6.1).
 *
 * @param requestId The request being decided.
 */
export function useApproveAccessRequest(requestId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ApproveAccessRequest) =>
      api.post<void>(`/authorization/access-requests/${requestId}/approve`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/**
 * Fetches the Clerk accept link for one approved request, so an operator can deliver it by hand.
 *
 * A mutation rather than a query, and deliberately: the link is a credential with no business being
 * prefetched, cached under a key, or refetched on a window focus. It is asked for at the moment somebody
 * decides to send it, held for that one interaction, and never invalidated into a list payload.
 *
 * The backend recovers the link from Clerk when the row predates it being stored — which is every
 * invitation that was already in flight — so this works on the rows that are stuck today, not only on
 * the ones invited from here on.
 */
export function useAccessRequestInviteLink() {
  return useMutation({
    mutationFn: (requestId: string) =>
      api.get<AccessRequestInviteLink>(`/authorization/access-requests/${requestId}/invite-link`),
  });
}

/**
 * "Reenviar convite": revokes whatever invitation the person still has and sends a new one — the way out
 * for an invitation that expired unread, never arrived, or was given up on after its retries (06 §6.1).
 * The old link stops working, which is why the screen confirms before calling this.
 */
export function useReissueAccessRequestInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) =>
      api.post<ReissueInviteResult>(`/authorization/access-requests/${requestId}/reissue-invite`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/** Rejects a request. Removing the person from the Clerk Waitlist is best-effort and never fails the call. */
export function useRejectAccessRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) =>
      api.post<void>(`/authorization/access-requests/${requestId}/reject`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/** A search only runs once the term is worth a round-trip; below this the picker stays idle. */
const MIN_SEARCH_LENGTH = 2;

/** How many rows a picker shows — enough to choose from, never a report. */
const PICKER_PAGE_SIZE = 8;

/**
 * Holdings matching a term, for scoping a grant to a group of Empresas (10 B6b).
 *
 * Gated by `users.manage` on the server — the caller hides the picker without it, since a 403 on every
 * keystroke is worse than not offering the scope at all.
 *
 * @param term Free text matched by the server against the holding name.
 */
export function useHoldingSearch(term: string): UseQueryResult<HoldingOption[]> {
  const trimmed = term.trim();
  const query = { search: trimmed, page: 1, pageSize: PICKER_PAGE_SIZE };

  return useQuery({
    queryKey: holdingKeys.search(query),
    queryFn: ({ signal }) => api.get<Paged<HoldingOption>>('/holdings', { query, signal }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    select: (page) => page.items,
  });
}
